import React, { useCallback } from 'react';
import { ThemeProvider } from '@mui/material';
import { Theme, createTheme } from '@mui/material/styles';
import {
  BirdzDialog,
  themeColors,
  useGlobalContext
} from '@applications-terrains/birdz-react-library';

/**
 * Options d'une confirmation, telles que les écrans les construisent.
 *
 * Déclarées ici plutôt que dans chaque panneau, où les trois appelants en avaient chacun
 * leur copie. Plus étroites que le type de la BRL : `confirmDialog` étant typé `any` chez
 * elle, rien ne garantirait sans cela qu'une option obligatoire n'est pas oubliée.
 */
export type ConfirmDialogOptions = {
  title: string;
  content: string;
  onValidate: () => void;
  onCancel: () => void;
};

type ConfirmDialogProps = {
  /**
   * L'objet rendu par `useDialog`, passé tel quel : il ajoute l'ouverture à nos options,
   * et part d'un objet vide avant la première confirmation. Son type est dérivé du
   * composant de la BRL, qui ne le réexporte pas à la racine du paquet — le recopier
   * finirait par diverger.
   */
  options: React.ComponentProps<typeof BirdzDialog>['options'];
};

/**
 * Modale de confirmation de l'écran, avec le titre à l'accent choisi par l'utilisateur —
 * le rendu des titres de modale de la charte, déjà appliqué par la modale d'ajout de
 * points de service.
 *
 * L'accent ne vit que dans le contexte global : un thème statique ne peut pas l'atteindre,
 * d'où ce fournisseur local. Il traverse bien le portail de la modale, qui est un portail
 * React et non un détachement du contexte. Le gras et la hiérarchie des boutons, eux,
 * viennent du thème de l'écran.
 */
export default function ConfirmDialog({ options }: ConfirmDialogProps) {
  const { theme } = useGlobalContext();
  const accent = themeColors[theme];

  const withAccentTitle = useCallback(
    (outerTheme: Theme) =>
      createTheme(outerTheme, {
        components: {
          MuiDialogTitle: {
            styleOverrides: {
              root: {
                color: accent
              }
            }
          }
        }
      }),
    [accent]
  );

  return (
    <ThemeProvider theme={withAccentTitle}>
      <BirdzDialog options={options} />
    </ThemeProvider>
  );
}
